import jsPDF from "jspdf";
import "jspdf-autotable";
import { toast } from "react-toastify";
import { getBarGraphData } from "../../app/api/overallAnalysis";
import {
  barGraphConfig,
  KEYS_TO_REMOVE_FOR_OVERALL_ANALYSIS_TABLE,
  mappedTrendFilterType,
  TrendsFilterType,
} from "../../constants/constants";
import { BarData } from "../../interface/Trends";
import { User } from "../../types";
import {
  getTrendFiltersForGraphs,
  getTrendFiltersFromSession,
} from "./graphHelperUtils";
import {
  capitalizeFirstLetterAndRemainingSmallForEachWord,
  getKeyFromValueForTrendQueryType,
} from "../commonUtils";

export const generateOverallAnalysisS3JSON = async (user: User | null) => {
  try {
    const trendFilters = getTrendFiltersForGraphs();
    if (!trendFilters) return null;
    const filterType = getKeyFromValueForTrendQueryType(
      trendFilters.query_type!
    );
    const overallAnalysisJSON = await formatAndSetDataForOverallAnalysis(
      filterType,
      user
    );
    if (!(Array.isArray(overallAnalysisJSON) && overallAnalysisJSON.length)) {
      console.error("No Overall Analysis Data");
      return null;
    }
    return {
      filterType,
      overallAnalysisJSON,
    };
  } catch (error) {
    console.error("Error while generating image", error);
    toast.error("Error while generating image", {
      hideProgressBar: true,
    });
    return null;
  }
};

export const formatAndSetDataForOverallAnalysis = async (
  filterType: TrendsFilterType,
  user: User | null,
  setBarData?: React.Dispatch<React.SetStateAction<BarData[] | null>>,
  setDynamicColumns?: React.Dispatch<React.SetStateAction<string[]>>
) => {
  const trendFilters = getTrendFiltersFromSession();
  if (!trendFilters) return null;
  const response = await getBarGraphData(trendFilters, user);
  if (!response) return;
  setBarData && setBarData(response as BarData[]);

  //Fetch the table contents
  if (setDynamicColumns && response?.length > 0) {
    const filteredKeys = Object.keys(response[0]).filter(
      key => !KEYS_TO_REMOVE_FOR_OVERALL_ANALYSIS_TABLE.includes(key)
    );
    setDynamicColumns(filteredKeys);
  }
  return response;
};

export const handleDownload = (
  setDownloadLoading: React.Dispatch<React.SetStateAction<boolean>>,
  barData: BarData[] | null,
  dynamicColumns: string[]
) => {
  setDownloadLoading(true);
  const svgElement = document.querySelector(".recharts-wrapper svg");

  if (!svgElement) {
    toast.error("No graph available to download");
    setDownloadLoading(false);
    return;
  }

  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  const width = svgElement.getBoundingClientRect().width;
  const height = svgElement.getBoundingClientRect().height;

  const scaleFactor = 4;
  canvas.width = width * scaleFactor;
  canvas.height = height * scaleFactor;
  context?.scale(scaleFactor, scaleFactor);

  const svgData = new XMLSerializer().serializeToString(svgElement);
  const img = new Image();
  img.onload = () => {
    context?.drawImage(img, 0, 0);
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const chartWidth = pageWidth * 0.9;
    const imgHeight = (canvas.height / canvas.width) * chartWidth;
    const xOffset = (pageWidth - chartWidth) / 2;
    pdf.setFontSize(12);

    pdf.text("Overall Analysis Graph", pageWidth / 2, 20, {
      align: "center",
    });
    pdf.addImage(imgData, "PNG", xOffset, 20, chartWidth, imgHeight);

    // Add color indications
    const legendX = pageWidth - 100;
    let legendY = 20;
    pdf.setFontSize(5);
    barGraphConfig.forEach(config => {
      pdf.setFillColor(config.color);
      pdf.setDrawColor(0, 0, 0);
      pdf.roundedRect(legendX + 30, legendY, 5, 5, 1, 1, "FD");
      pdf.text(`${config.bet}`, legendX + 40, legendY + 5);
      legendY += 10;
    });
    legendY = 40 + imgHeight;

    pdf.setFontSize(12);
    pdf.text("Overall Analysis Table", pageWidth / 2, legendY + 20, {
      align: "center",
    });

    const tableData = barData?.map((row, index) => [
      row.tNo,
      Number(row.contribution).toFixed(2),
      row.impact,
      row.bet,
      ...dynamicColumns.map(column =>
        typeof row[column] === "string"
          ? capitalizeFirstLetterAndRemainingSmallForEachWord(row[column])
          : column === "Impact Score"
            ? Number(row[column]).toFixed(0)
            : Number(row[column]).toFixed(2)
      ),
    ]);

    const tableHead = [
      ["Trend No.", "Contribution", "Impact", "Bet", ...dynamicColumns],
    ];

    // @ts-expect-error: autoTable is not recognized by TypeScript
    pdf.autoTable({
      head: tableHead,
      body: tableData || [],
      startY: legendY + 40,
      theme: "grid",
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [77, 77, 77],
        fontStyle: "bold",
        halign: "center",
        padding: 2,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [77, 77, 77],
        halign: "center",
        padding: 4,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
      styles: {
        fontSize: 10,
        cellPadding: { top: 4, right: 2, bottom: 4, left: 2 },
      },
      tableWidth: chartWidth,
    });

    pdf.save("Overall Analysis.pdf");
    setDownloadLoading(false);
  };
  img.src = "data:image/svg+xml;base64," + btoa(svgData);
};
